'use client'

import * as React from 'react'
import { LuCheck, LuChevronDown, LuGroup } from 'react-icons/lu'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { useMetadataKeys, useCachedMetadataKeys } from '@/data/hooks/api/useMetadata'

export type GroupByValue = 'modality' | 'provider' | 'model' | string

interface GroupByOption {
  value: GroupByValue
  label: string
  description?: string
}

// Static options that are always available
const staticOptions: GroupByOption[] = [
  {
    value: 'modality',
    label: 'Modality',
    description: 'Text, image, audio, video, etc.',
  },
  {
    value: 'provider',
    label: 'Provider',
    description: 'OpenAI, Anthropic, Google, etc.',
  },
  {
    value: 'model',
    label: 'Model',
    description: 'gpt-4o, claude-3.5-sonnet, gemini-2.0, etc.',
  },
]

// Description shown under the Metadata Keys group header
const metadataKeysDescription = 'Custom tags sent with each event to Fenra'

interface GroupBySelectorProps {
  value?: GroupByValue
  onChange?: (value: GroupByValue) => void
  className?: string
}

const GroupBySelector = ({
  value: externalValue,
  onChange: externalOnChange,
  className,
}: GroupBySelectorProps) => {
  const [open, setOpen] = React.useState(false)

  // Internal state for uncontrolled mode
  const [internalValue, setInternalValue] = React.useState<GroupByValue>('modality')

  // Fetch metadata keys
  const { data: metadataKeysData } = useMetadataKeys()

  // Get cached keys from React Query
  const cachedKeys = useCachedMetadataKeys()

  // Get dynamic metadata options
  const metadataKeyOptions = React.useMemo(() => {
    // Get keys from API data first
    const apiKeys = metadataKeysData?.keys?.map(k => k.key) || []

    // Combine with cached keys and deduplicate
    const allKeys = [...new Set([...apiKeys, ...cachedKeys])]

    return allKeys.map(key => ({
      value: `metadata:${key}`,
      label: key
    }))
  }, [metadataKeysData, cachedKeys])

  // Use external value if provided, otherwise use internal state
  const value = externalValue !== undefined ? externalValue : internalValue
  const onChange = externalOnChange || setInternalValue

  // Get display text for the button
  const getDisplayText = () => {
    // Check static options first
    const staticOption = staticOptions.find((opt) => opt.value === value)
    if (staticOption) {
      return staticOption.label
    }

    // Check metadata options
    const metadataOption = metadataKeyOptions.find((opt) => opt.value === value)
    if (metadataOption) {
      return metadataOption.label
    }

    // Handle case where value might be a metadata key not in current options
    if (typeof value === 'string' && value.startsWith('metadata:')) {
      return value.replace('metadata:', '')
    }

    return 'Modality'
  }

  // Handle option selection
  const handleSelect = (optionValue: GroupByValue) => {
    onChange(optionValue)
    setOpen(false)
  }

  // Check if a value is selected
  const isSelected = (optionValue: GroupByValue) => value === optionValue

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="transparent-outline"
          size="xs"
          className={cn(
            'hover:bg-[#F2EEFF] hover:text-[#654EDA] hover:border-[#E2DAFF]',
            'focus-visible:ring-[#654EDA]/20 focus-visible:border-[#654EDA] focus-visible:ring-2',
            open &&
            'bg-[#E2DAFF] text-[#654EDA] border-[#C6B9FF] hover:bg-[#E2DAFF] focus-visible:ring-0',
            className
          )}
        >
          <LuGroup className="size-4" />
          {getDisplayText()}
          <LuChevronDown
            className={cn(
              'size-3.5 ml-0.5 transition-transform duration-200',
              open && 'rotate-180'
            )}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[280px] p-0 border-[#EDEEF3] shadow-lg"
        align="start"
        sideOffset={6}
      >
        {/* Header */}
        <div className="px-3 py-2 border-b border-[#EDEEF3]">
          <span className="text-[11px] font-medium text-[#8A8F98] uppercase tracking-wide">
            Group by
          </span>
        </div>

        {/* Static Options */}
        <div className="py-1">
          {staticOptions.map((option) => {
            const selected = isSelected(option.value)

            return (
              <div
                key={option.value}
                className={cn(
                  'flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors',
                  'hover:bg-[#F7F8F8]',
                  selected && 'bg-[#F7F8F8]'
                )}
                onClick={() => handleSelect(option.value)}
              >
                <div className="flex-1 min-w-0">
                  <span
                    className={cn(
                      'text-[13px] font-medium block',
                      selected ? 'text-[#654EDA]' : 'text-[#08090A]'
                    )}
                  >
                    {option.label}
                  </span>
                  {option.description && (
                    <span className="text-[11px] text-[#8A8F98] block mt-0.5">
                      {option.description}
                    </span>
                  )}
                </div>
                {selected && (
                  <LuCheck className="size-4 text-[#654EDA] shrink-0" />
                )}
              </div>
            )
          })}
        </div>

        {/* Metadata Keys Section */}
        {metadataKeyOptions.length > 0 && (
          <>
            {/* Metadata Group Header */}
            <div className="px-3 py-2 border-t border-b border-[#EDEEF3]">
              <span className="text-[11px] font-medium text-[#8A8F98] uppercase tracking-wide block">
                Metadata Keys
              </span>
              <span className="text-[11px] text-[#8A8F98] block mt-0.5">
                {metadataKeysDescription}
              </span>
            </div>

            {/* Metadata Options */}
            <div className="py-1">
              {metadataKeyOptions.map((option) => {
                const selected = isSelected(option.value)

                return (
                  <div
                    key={option.value}
                    className={cn(
                      'flex items-center gap-3 px-3 h-[32px] cursor-pointer transition-colors',
                      'hover:bg-[#F7F8F8]',
                      selected && 'bg-[#F7F8F8]'
                    )}
                    onClick={() => handleSelect(option.value)}
                  >
                    <span
                      className={cn(
                        'text-[13px] font-medium flex-1',
                        selected ? 'text-[#654EDA]' : 'text-[#08090A]'
                      )}
                    >
                      {option.label}
                    </span>
                    {selected && (
                      <LuCheck className="size-4 text-[#654EDA] shrink-0" />
                    )}
                  </div>
                )
              })}
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  )
}

export { GroupBySelector }

